import React, { useState, useEffect } from "react";
import { Descriptions, Button, Flex, Select, notification, Input, Typography, Modal, Tabs, Form } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import axiosInstance from "../../../utilities/axios";
import EditStyleModal from "./EditStyleModal";

const { Title } = Typography;

const ProductModal = ({ product, visible, onClose, onChange }) => {
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const [editMode, setEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [activeKey, setActiveKey] = useState("0");
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (!product) return;
  
    axiosInstance
      .get(`/products/product-lines/${product.id}`)
      .then((response) => {
        const fetchedProduct = response.data;
        setInitialValues({
          brand: fetchedProduct?.brand,
          family: fetchedProduct?.family,
          name: fetchedProduct?.name,
          techLevel: fetchedProduct?.techLevel,
          styles: fetchedProduct?.styles || [],
        });
        form.setFieldsValue({
          brand: fetchedProduct?.brand,
          family: fetchedProduct?.family,
          name: fetchedProduct?.name,
          techLevel: fetchedProduct?.techLevel,
        });
      })
      .catch((error) => {
        notification.error({
          message: "Error fetching product details",
          description: error.message,
        });
      });
  }, [product]);

  const saveChanges = () => {
    const currentValues = form.getFieldsValue();
    const formData = new FormData();

    for (const key in currentValues) {
      if (currentValues[key] != initialValues[key]) {
        formData.append(key, currentValues[key]);
      }
    }

    axiosInstance
      .put(`/products/product-lines/${product.id}`, formData)
      .then((response) => {
        onChange();
        setEditMode(false);
        onClose();
        notification.success({
          message: "Product updated successfully",
          description: `${initialValues.name} has been updated.`,
        });
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  const handleClose = () => {
    setEditMode(false);
    onClose();
    setActiveKey("0");
    form.resetFields();
    setInitialValues({});
  };

  return (
    <Modal
      title="Product Details"
      open={visible}
      width={800}
      onCancel={handleClose}
      onClose={handleClose}
      footer={
        <Button
          key="close"
          onClick={handleClose}
        >
          Close
        </Button>
      }
      styles={{ content: { overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' } }}
    >
      <Form
        form={form}
        layout="vertical"
        name="productForm"
        onFinish={saveChanges}
      >
        <Descriptions
          bordered
          column={4}
        >
          <Descriptions.Item
            label="Brand"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="brand"
                rules={[{ required: true, message: "Please enter a brand" }]}
                style={{ marginBottom: 0 }}
              >
                <Input style={{ width: "100%" }} />
              </Form.Item>
            ) : (
              initialValues.brand || "No brand available"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Product Family"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="family"
                rules={[{ required: true, message: "Please enter a product family" }]}
                style={{ marginBottom: 0 }}
              >
                <Input style={{ width: "100%" }} />
              </Form.Item>
            ) : (
              initialValues.family || "No product family available"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Product Line"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="name"
                rules={[{ required: true, message: "Please enter a product line" }]}
                style={{ marginBottom: 0 }}
              >
                <Input style={{ width: "100%" }} />
              </Form.Item>
            ) : (
              initialValues.name || "No name available"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Tech Level"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="techLevel"
                rules={[{ required: true, message: "Please select a tech level" }]}
                style={{ marginBottom: 0 }}
              >
                <Select style={{ width: "100%" }}>
                  <Select.Option value="1">Standard</Select.Option>
                  <Select.Option value="2">Advanced</Select.Option>
                  <Select.Option value="3">Premium</Select.Option>
                  <Select.Option value="4">Everyday Low Price</Select.Option>
                  <Select.Option value="5">Premium Plus</Select.Option>
                </Select>
              </Form.Item>
            ) : (
              initialValues.techLevel || "No tech level available"
            )}
          </Descriptions.Item>
        </Descriptions>
        <Flex
          justify="flex-end"
          style={{ marginTop: "10px", gap: "10px" }}
        >
          <Button
            key="save"
            htmlType="submit"
            type="primary"
            disabled={
              editMode &&
              allValues.brand === initialValues.brand &&
              allValues.family === initialValues.family &&
              allValues.name === initialValues.name &&
              allValues.techLevel === initialValues.techLevel
            }
            style={{ display: editMode ? "block" : "none" }}
          >
            Save Changes
          </Button>
          <Button
            key="edit"
            type={editMode ? "dashed" : "text"}
            icon={<EditOutlined />}
            onClick={() => {
              if (editMode) {
                form.resetFields();
                form.setFieldsValue(initialValues);
              }
              setEditMode(!editMode);
            }}
            style={{ display: initialValues.status === "discontinued" ? "none" : "block" }}
          />
        </Flex>
      </Form>
      <Title level={5} style={{ marginTop: 0 }}>Styles</Title>
      <Tabs
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        type="card"
        items={initialValues.styles?.map((style, index) => ({
          label: style?.name,
          key: `${index}`,
          children: (
            <>
              <Descriptions
                bordered
                column={4}
              >
                <Descriptions.Item label="MSRP" span={2}>
                  {style?.currentMSRP?.msrp
                    ? `$${style?.currentMSRP?.msrp.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : style?.futureMSRP?.msrp
                    ? `$${style?.futureMSRP?.msrp.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "No MSRP available"}
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={2}>
                  {style.status === "available" ? "Available" : style.status === "discontinued" ? "Discontinued" : "Pending"}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date" span={2}>
                  {style?.currentMSRP?.startDate
                    ? dayjs(style?.currentMSRP?.startDate).format("YYYY-MM-DD")
                    : style?.futureMSRP?.startDate
                    ? dayjs(style?.futureMSRP?.startDate).format("YYYY-MM-DD")
                    : "No start date available"}
                </Descriptions.Item>
                <Descriptions.Item label="End Date" span={2}>
                  {style?.currentMSRP?.endDate
                    ? dayjs(style?.currentMSRP?.endDate).format("YYYY-MM-DD")
                    : style?.futureMSRP?.endDate
                    ? dayjs(style?.futureMSRP?.endDate).format("YYYY-MM-DD")
                    : "No end date available"}
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={4}>
                  {style?.description || "No description available"}
                </Descriptions.Item>
                <Descriptions.Item label="Photo" span={4}>
                  {style?.photo ? (
                    <img src={style?.photo} alt={style?.name} style={{ width: "100px", height: "100px", objectFit: "cover" }} />
                  ) : (
                    "No photo available"
                  )}
                </Descriptions.Item>
              </Descriptions>
              <Flex
                justify="flex-end"
                style={{ marginTop: "10px", gap: "10px" }}
              >
                <Button
                  key="edit"
                  type={editMode ? "dashed" : "text"}
                  icon={<EditOutlined />}
                  onClick={() => setShowEditModal(true)}
                  style={{ display: initialValues.status === "discontinued" ? "none" : "block" }}
                />
              </Flex>
              <EditStyleModal
                product={style}
                visible={showEditModal}
                onClose={() => setShowEditModal(false)}
                onChange={() => {
                  axiosInstance
                    .get(`/products/product-lines/${product.id}`)
                    .then((response) => {
                      const fetchedProduct = response.data;
                      setInitialValues({
                        brand: fetchedProduct?.brand,
                        family: fetchedProduct?.family,
                        name: fetchedProduct?.name,
                        techLevel: fetchedProduct?.techLevel,
                        styles: fetchedProduct?.styles || [],
                      });
                      form.setFieldsValue({
                        brand: fetchedProduct?.brand,
                        family: fetchedProduct?.family,
                        name: fetchedProduct?.name,
                        techLevel: fetchedProduct?.techLevel,
                      });
                      notification.success({
                        message: "Style updated successfully",
                        description: `${style.name} has been updated.`,
                      });
                    })
                    .catch((error) => {
                      notification.error({
                        message: "Error fetching product details",
                        description: error.message,
                      });
                    });
                }}
              />
            </>
          )
        }))}
      />
    </Modal>
  );
}

export default ProductModal;